import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useWebAuthn } from 'react-hook-webauthn';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ListView } from "@progress/kendo-react-listview";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { getCSRFToken } from '../../app/getCSRFToken';
import { LoadingPanel } from "../LoadingPanel";
import { authenticationService } from '../../services/authenticationService';

import {
    FormInput,
    FormDropDownList
} from "../forms/form-components";

export const UserMyLogin = () => {
    const dispatch = useDispatch();

    const rpOptions = {
        rpId: window.location.hostname,
        rpName: "OTT",
    }

    const { getCredential } = useWebAuthn(rpOptions);

    const currentUser = authenticationService.currentUserValue;

    const [isLoading, setIsLoading] = useState(true);
    const [loginData, setLoginData] = useState({});
    const [timeZones, setTimeZones] = useState([]);
    const [enteredDeviceName, setEnteredDeviceName] = useState(undefined);
    const [emailAddress, setEmailAddress] = useState(undefined);
    const [authenticatedDevices, setAuthenticatedDevices] = useState([]);

    const getTimeZones = () => {
        fetch(`/api/general/timezones`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {                
                setTimeZones(result);    
                
                getLogin(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                setIsLoading(false);
            });
    }

    const getLogin = (timeZonesLocal) => {
        if (!currentUser) {
            return;
        }

        const dataItem = {
            loginKey: currentUser.loginKey,
        };

        fetch(`/api/user/getlogin`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                let resultCopy = result;
                resultCopy.timeZone = timeZonesLocal.find(element => element.timeZoneId === (resultCopy.timeZoneId ?? "UTC"));

                setLoginData(resultCopy);
                setEmailAddress(resultCopy.emailAddress);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const nameValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const passwordValidator = (value) => {
        if (!value || value.length === 0) {
            return "";
        }

        if (value.length < 8) {
            return "Minimum length is 8 characters";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const timeZoneValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        return "";
    }

    const handleSubmit = (dataItem) => {   
        dataItem.timeZoneId = dataItem.timeZone.timeZoneId;

        fetch("/api/user/login",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated your login info successfully");
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const authenticateDevice = useCallback(async () => {
        if (!currentUser) {
            return;
        }

        if (!enteredDeviceName) {
            addNotificationWithTimeout(dispatch, "error", "The device name is required");
            return;
        }

        if (enteredDeviceName.length > 50) {
            addNotificationWithTimeout(dispatch, "error", "The maximum device name length is 50 characters");
            return;
        }

        const credential = await getCredential({
            challenge: 'stringFromServer',
            userDisplayName: emailAddress,
            userId: emailAddress,
            userName: emailAddress
        });

        //Save the authentication key to the login
        setIsLoading(true);

        const dataItem = {
            loginKey: currentUser.loginKey,
            name: enteredDeviceName,
            authenticationKey: credential.id,
        }

        fetch("/api/user/loginauthenticationkey",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Saved authenticated device successfully");

                getAuthenticatedDevices();
                setEnteredDeviceName("");
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [getCredential, emailAddress]);

    const removeAuthenticatedDevice = (key) => {
        if (window.confirm("Are you sure you want to delete this authenticated device?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            loginAuthenticationKeyKey: key
        }

        fetch("/api/user/loginauthenticationkey",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted authenticated device successfully");

                getAuthenticatedDevices();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const deviceNameChanged = (event) => {
        setEnteredDeviceName(event.value);
    };

    const AuthenticatedDeviceRender = (props) => {
        const item = props.dataItem;

        return (
            <div className="mb-2">
                {item.name}
                &nbsp;&nbsp;
                <Button type={"button"} onClick={() => removeAuthenticatedDevice(item.loginAuthenticationKeyKey)} icon="trash" fillMode="flat" />
            </div>
        );
    };

    const getAuthenticatedDevices = () => {
        if (!currentUser) {
            return;
        }

        const dataItem = {
            loginKey: currentUser.loginKey,
        };

        fetch(`/api/user/loginauthenticationkeys`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAuthenticatedDevices(result.loginAuthenticationKeys);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const logOutAllDevices = () => {
        if (window.confirm("Are you sure you log out all devices?") === false) {
            return;
        }

        const dataItem = {
            loginKey: currentUser.loginKey,
        };

        fetch(`/api/user/logoutalldevices`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Logged out of all devices successfully");

                window.location = "/logout";
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    useEffect(() => {
        document.title = "My Login";

        getTimeZones();
        getAuthenticatedDevices();
    }, []);

    return (
        <div className="noSplitterContent">
            {isLoading ? <LoadingPanel /> : null}
            <Form
                onSubmit={handleSubmit}
                initialValues={loginData}
                key={JSON.stringify(loginData)}
                render={(formRenderProps) => (
                    <FormElement>
                        <h2>
                            My Login
                        </h2>
                        <fieldset className={"k-form-fieldset"}>
                            <div className="mb-3 noFormField">
                                <Label>Key:</Label> {loginData.loginKey}
                            </div>
                            <div className="mb-3 noFormField">
                                <Label>Email address:</Label> {loginData.emailAddress}
                            </div>
                            <Field
                                id={"timeZone"}
                                name={"timeZone"}
                                label={"Time zone"}
                                component={FormDropDownList}
                                data={timeZones}
                                textField="name"
                                dataItemKey="timeZoneId"
                                validator={timeZoneValidator}
                            /> 

                            <Field
                                id={"firstName"}
                                name={"firstName"}
                                label={"First name"}
                                component={FormInput}
                                validator={nameValidator}
                            />

                            <Field
                                id={"lastName"}
                                name={"lastName"}
                                label={"Last name"}
                                component={FormInput}
                                validator={nameValidator}
                            />

                            <Field
                                id={"password"}
                                name={"password"}
                                label={"Password"}
                                component={FormInput}
                                validator={passwordValidator}
                                type={"password"}
                                showPasswordToggle={true}
                                autoComplete="new-password"
                                aria-label={"Password"}
                                hint={"Leave blank if not changing. Eight or more characters."}
                            />
                        </fieldset>
                        <span className={"k-form-separator"} />
                        <div className="k-form-buttons">
                            <Button
                                themeColor={"primary"}
                                type={"submit"}
                                disabled={!formRenderProps.allowSubmit}
                            >
                                Save
                            </Button>

                            <Button
                                type={"button"}
                                className={"deleteButton"}
                                onClick={() => logOutAllDevices()}
                            >
                                Log Out All Devices
                            </Button>
                        </div>
                    </FormElement>
                )}
            />

            <span className="k-form-separator mb-2" />

            <div>
                <h4>
                    Authenticated Devices
                </h4>
                <ListView
                    data={authenticatedDevices}
                    item={AuthenticatedDeviceRender}
                    style={{
                        width: "100%",
                    }}
                />
                <div className="row">                    
                    <div className="col-6 col-md-5">
                        <Input
                            id={"deviceName"}
                            placeholder="Name"
                            onChange={deviceNameChanged}
                            value={enteredDeviceName}
                        />
                    </div>
                    <div className="col-6 col-md-7">
                        <Button
                            themeColor={"primary"}
                            type={"button"}
                            iconClass="fa-solid fa-fingerprint"
                            onClick={authenticateDevice}
                        >
                            Authenticate this Device
                        </Button>
                    </div>                    
                </div>
            </div>
        </div>
    );
}